import { useState } from 'react';
import { pushAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { Megaphone, ShieldAlert, Send, Loader2, CheckCircle } from 'lucide-react';

const CATEGORIES = [
  { value: 'recalls', label: 'Safety Recall / Notice (sent to everyone)' },
  { value: 'system_updates', label: 'System / Product Update' },
  { value: 'warranty_updates', label: 'Warranty Update' },
  { value: 'newsletters', label: 'Newsletter' },
];

export const SendNotificationPage = () => {
  const [form, setForm] = useState({ category: 'recalls', title: '', body: '', url: '/my-portal' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    const isRecall = form.category === 'recalls';
    if (!window.confirm(`Send this ${isRecall ? 'SAFETY RECALL' : 'notification'} to subscribed customers now?`)) return;
    setSending(true);
    setResult(null);
    try {
      const res = await pushAPI.broadcast(form);
      setResult(res.data);
      toast.success(`Sent to ${res.data.recipients} customer(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const isRecall = form.category === 'recalls';

  return (
    <div className="max-w-2xl space-y-6 animate-fade-in" data-testid="send-notification-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] heading-tight flex items-center gap-2">
          <Megaphone className="h-7 w-7 text-blue-600" /> Send Customer Notification
        </h1>
        <p className="text-muted-foreground mt-1">Push an alert to customers&apos; phones/devices (and their in-app inbox).</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Compose</CardTitle>
          <CardDescription>
            Safety recalls are delivered to <strong>every</strong> customer (they can&apos;t opt out). Other categories respect each customer&apos;s preferences.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Type</Label>
            <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
              <SelectTrigger className="mt-1" data-testid="notif-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isRecall && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
              This will be sent as a mandatory safety notice to all subscribed customers.
            </div>
          )}
          <div>
            <Label htmlFor="title">Title</Label>
            <Input id="title" placeholder="e.g., Safety Recall: Model X Vacuum Pump"
              value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              className="mt-1" data-testid="notif-title" />
          </div>
          <div>
            <Label htmlFor="body">Message</Label>
            <Textarea id="body" rows={4} placeholder="Describe the recall / notice and any action the customer should take."
              value={form.body} onChange={(e) => setForm(p => ({ ...p, body: e.target.value }))}
              className="mt-1" data-testid="notif-body" />
          </div>
          <div>
            <Label htmlFor="url">Link (where it opens)</Label>
            <Input id="url" value={form.url} onChange={(e) => setForm(p => ({ ...p, url: e.target.value }))}
              className="mt-1" data-testid="notif-url" />
            <p className="text-xs text-muted-foreground mt-1">e.g. /my-portal/resources or /my-portal/support</p>
          </div>

          <Button onClick={handleSend} disabled={sending}
            className={isRecall ? 'bg-red-600 hover:bg-red-700' : ''} data-testid="send-notif-btn">
            {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : <><Send className="h-4 w-4 mr-2" /> Send Notification</>}
          </Button>

          {result && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="notif-result">
              <CheckCircle className="h-4 w-4" />
              Delivered to {result.recipients} customer(s) — {result.pushes_sent} device push(es) sent, plus in-app alerts.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SendNotificationPage;
